import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AppContext';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { Heart, MoreHorizontal, Play, Plus, ListMusic, Trash2, Eye, User, Flag, ThumbsDown } from 'lucide-react';
import { toast } from 'sonner';
import { motion } from 'framer-motion';
import { ReportModal } from './ReportModal';
import { AIDisclosureBadges } from './AIDisclosure';
import { FoundingCreatorBadge } from './FoundingCreatorBadge';
import { FounderBadge } from './FounderBadge';
import { isVideoProcessing } from '../utils/hlsPlayer';
import { useLikeAuthGate } from './AuthPromptModal';

export const VideoCard = ({ video, thumbnail, onClick, showRemove = false, onRemove, onNotInterested, onArtistClick }) => {
  const navigate = useNavigate();
  const [isLiked, setIsLiked] = useState(video.is_liked || false);
  const [isHidden, setIsHidden] = useState(false);
  const [playlists, setPlaylists] = useState([]);
  const [showReportModal, setShowReportModal] = useState(false);
  const { axiosInstance, user } = useAuth();
  const requireAuthForLike = useLikeAuthGate();

  const handleLike = async (e) => {
    e.stopPropagation();
    // Single shared gate (same modal copy across every surface) — fires
    // BEFORE we flip any UI state, so a logged-out user never sees a
    // half-completed like animation that vanishes on re-render.
    if (!requireAuthForLike(user, video?.id)) return;

    try {
      if (isLiked) {
        await axiosInstance.delete(`/favorites/${video.id}`);
        setIsLiked(false);
        toast.success('Removed from favorites');
      } else {
        await axiosInstance.post(`/favorites/${video.id}`);
        setIsLiked(true);
        toast.success('Added to favorites');
      }
    } catch (err) {
      toast.error('Failed to update favorites');
    }
  };

  const handleNotInterested = async (e) => {
    e.stopPropagation();
    if (!user) {
      toast.error('Please sign in to use this feature');
      return;
    }

    try {
      await axiosInstance.post(`/videos/${video.id}/not-interested`);
      setIsHidden(true);
      toast.success("Got it! We'll show you less like this");
      if (onNotInterested) {
        onNotInterested(video.id);
      }
    } catch (err) {
      toast.error('Failed to update preferences');
    }
  };

  const fetchPlaylists = async () => {
    if (!user) return;
    try {
      const res = await axiosInstance.get('/playlists');
      setPlaylists(res.data);
    } catch (err) {
      console.error('Failed to fetch playlists');
    }
  };

  const addToPlaylist = async (playlistId) => {
    try {
      await axiosInstance.post(`/playlists/${playlistId}/videos/${video.id}`);
      toast.success('Added to playlist');
    } catch (err) {
      toast.error('Failed to add to playlist');
    }
  };

  const formatViews = (views) => {
    if (views >= 1000000) return `${(views / 1000000).toFixed(1)}M`;
    if (views >= 1000) return `${(views / 1000).toFixed(1)}K`;
    return views?.toString() || '0';
  };

  // If user marked as not interested, hide the card
  if (isHidden) {
    return null;
  }

  return (
    <>
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="group bg-zinc-900/50 border border-zinc-800 rounded-xl overflow-hidden card-hover cursor-pointer"
      onClick={isVideoProcessing(video) ? undefined : onClick}
      data-testid={`video-card-${video.id}`}
    >
      {/* Thumbnail */}
      <div className="relative aspect-video bg-zinc-800">
        <img
          src={thumbnail}
          alt={video.title}
          className="w-full h-full object-cover"
          loading="lazy"
        />

        {/* Processing overlay — shown while Bunny is still transcoding so
            viewers don't tap into a broken player. The card stays in the feed
            so the creator sees their upload immediately; it just doesn't play
            until the webhook flips status to "approved". */}
        {isVideoProcessing(video) && (
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm flex flex-col items-center justify-center gap-2"
            data-testid={`video-processing-${video.id}`}
          >
            <div className="w-9 h-9 border-2 border-fuchsia-400 border-t-transparent rounded-full animate-spin" />
            <span className="text-xs text-white font-medium">Processing…</span>
            <span className="text-[10px] text-zinc-400 text-center px-3">Your video is processing — this may take up to 10 minutes</span>
          </div>
        )}
        
        {/* Play overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
          <div className="w-14 h-14 rounded-full bg-white/20 backdrop-blur-md flex items-center justify-center transform scale-90 group-hover:scale-100 transition-transform">
            <Play className="w-7 h-7 text-white fill-white ml-1" />
          </div>
        </div>

        {/* Like button */}
        <button
          onClick={handleLike}
          className="absolute top-2 right-2 p-2 rounded-full bg-black/40 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-black/60"
          data-testid={`like-btn-${video.id}`}
        >
          <Heart className={`w-5 h-5 ${isLiked ? 'text-pink-500 fill-pink-500' : 'text-white'}`} />
        </button>
      </div>

      {/* Info */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <h3 className="text-white font-semibold truncate" title={video.title}>
              {video.title}
            </h3>
            <div className="flex items-center gap-2">
              <button 
                className="text-zinc-400 text-sm truncate hover:text-white hover:underline transition-colors"
                onClick={(e) => {
                  e.stopPropagation();
                  if (onArtistClick) onArtistClick(video);
                }}
                data-testid={`artist-link-${video.id}`}
              >
                {video.artist}
              </button>
              {video.creator_tier && (
                <span 
                  className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-xs font-medium"
                  style={{ 
                    background: `${video.creator_tier.color}20`,
                    color: video.creator_tier.color
                  }}
                  title={video.creator_tier.name}
                >
                  <span>{video.creator_tier.badge}</span>
                </span>
              )}
              {video.creator_tier?.is_founder ? (
                <FounderBadge size="sm" />
              ) : (
                <FoundingCreatorBadge number={video.creator_tier?.founding_number} />
              )}
            </div>
            <div className="flex items-center gap-3 mt-2 text-zinc-500 text-xs">
              <span className="flex items-center gap-1">
                <Eye className="w-3.5 h-3.5" />
                {formatViews(video.views)}
              </span>
              <span className="flex items-center gap-1">
                <Heart className="w-3.5 h-3.5" />
                {formatViews(video.likes)}
              </span>
              <span className="px-2 py-0.5 rounded-full bg-zinc-800 text-zinc-400">
                {video.genre}
              </span>
            </div>
          </div>

          {/* Menu */}
          <DropdownMenu onOpenChange={(open) => open && fetchPlaylists()}>
            <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
              <Button variant="ghost" size="icon" className="text-zinc-500 hover:text-white h-8 w-8">
                <MoreHorizontal className="w-5 h-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="bg-zinc-900 border-zinc-800 w-48" align="end">
              {user && (
                <>
                  <DropdownMenuItem
                    className="text-zinc-300 hover:text-white cursor-pointer"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleLike(e);
                    }}
                  >
                    <Heart className={`w-4 h-4 mr-2 ${isLiked ? 'text-pink-500 fill-pink-500' : ''}`} />
                    {isLiked ? 'Unlike' : 'Like'}
                  </DropdownMenuItem>
                  
                  {playlists.length > 0 && (
                    <>
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      <div className="px-2 py-1.5 text-xs text-zinc-500 font-medium">Add to playlist</div>
                      {playlists.map(pl => (
                        <DropdownMenuItem
                          key={pl.id}
                          className="text-zinc-300 hover:text-white cursor-pointer"
                          onClick={(e) => {
                            e.stopPropagation();
                            addToPlaylist(pl.id);
                          }}
                        >
                          <ListMusic className="w-4 h-4 mr-2" />
                          {pl.name}
                        </DropdownMenuItem>
                      ))}
                    </>
                  )}

                  {showRemove && onRemove && (
                    <>
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      <DropdownMenuItem
                        className="text-red-400 hover:text-red-300 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemove();
                        }}
                      >
                        <Trash2 className="w-4 h-4 mr-2" />
                        Remove
                      </DropdownMenuItem>
                    </>
                  )}

                  {user && video.user_id !== user.id && (
                    <>
                      <DropdownMenuSeparator className="bg-zinc-800" />
                      <DropdownMenuItem
                        className="text-yellow-400 hover:text-yellow-300 cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          setShowReportModal(true);
                        }}
                      >
                        <Flag className="w-4 h-4 mr-2" />
                        Report
                      </DropdownMenuItem>
                      <DropdownMenuItem
                        className="text-zinc-400 hover:text-zinc-200 cursor-pointer"
                        onClick={handleNotInterested}
                        data-testid={`not-interested-${video.id}`}
                      >
                        <ThumbsDown className="w-4 h-4 mr-2" />
                        Not Interested
                      </DropdownMenuItem>
                    </>
                  )}
                </>
              )}
              
              <DropdownMenuItem className="text-zinc-300 hover:text-white cursor-pointer">
                <User className="w-4 h-4 mr-2" />
                {video.username}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    </motion.div>

    <ReportModal
      isOpen={showReportModal}
      onClose={() => setShowReportModal(false)}
      video={video}
    />
    </>
  );
};
